import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { STATUS_CFG } from './statusConfig';
import { fmtDate } from './helpers';

// ── Fungsi Export ke Excel ──
export const exportToExcel = async (
    tasks, category, accentColor, displayProject, displayTitle, computedStatusCfg, displayDueDate
) => {
    try {
        // Validasi: pastikan ada data task
        if (!tasks || tasks.length === 0) {
            alert('Tidak ada data task untuk diekspor.');
            return;
        }

        // Warna aksen project (dari category yang sedang dibuka)
        const accentArgb = toArgb(accentColor);

        // Ambil daftar team members (PIC unik dari semua task)
        const uniquePics = [...new Set(
            tasks.map(t => {
                const pic = t.pic?.split('-');
                return pic && pic.length > 1 ? pic[1].trim() : t.pic?.trim();
            }).filter(Boolean)
        )];
        const teamMembersStr = uniquePics.length > 0 ? uniquePics.join(', ') : '-';

        // Nama Project Manager dari displayProject.pic
        const splitProjectManager = displayProject.pic ? displayProject.pic.replace(/"/g, '').split('-').map(s => s.trim()) : [];
        const len = splitProjectManager.length;
        const projectManagerName = len === 0 ? '-'
            : `${splitProjectManager[1]} / ${splitProjectManager[len - 1]}`;

        // ── Buat workbook & worksheet ──
        const workbook  = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Data Project');

        // Set lebar kolom
        worksheet.columns = [
            { key: 'a', width: 42 }, // TASK
            { key: 'b', width: 30 }, // PIC
            { key: 'c', width: 22 }, // PIC EXTERNAL
            { key: 'd', width: 20 }, // STATUS
            { key: 'e', width: 28 }, // SCHEDULE DATE
            { key: 'f', width: 28 }, // ACTUAL DATE
            { key: 'g', width: 12 }, // PROGRESS
            { key: 'h', width: 14 }, // DURASI
        ];

        // Total kolom tabel = 8 (A sampai H)
        const TOTAL_COLS = 8;
        // ── Judul utama (baris 1) — merge A1:G1 ──
        // Format: "Data Project - {Nama Kategori}"
        const projectTitle = `Data Project - ${category.name || 'Project'}`;
        worksheet.addRow([projectTitle]);
        worksheet.mergeCells(1, 1, 1, TOTAL_COLS);
        const titleRow  = worksheet.getRow(1);
        titleRow.height = 36;
        const titleCell = titleRow.getCell(1);
        titleCell.font      = { name: 'Calibri', size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
        titleCell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentArgb } };
        titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
        titleCell.border    = thinBorder(accentArgb);

        // ── Header seksi "Informasi Project" (baris 2) — merge A2:G2 ──
        worksheet.addRow(['INFORMASI PROJECT']);
        worksheet.mergeCells(2, 1, 2, TOTAL_COLS);
        const sectionRow  = worksheet.getRow(2);
        sectionRow.height = 22;
        const sectionCell = sectionRow.getCell(1);
        sectionCell.font      = { name: 'Calibri', size: 11, bold: true, color: { argb: accentArgb } };
        sectionCell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
        sectionCell.alignment = { vertical: 'middle', horizontal: 'left' };
        sectionCell.border    = thinBorder();

        // ── Helper untuk baris info project ──
        // Label di kolom A, value di kolom B–G (merge B:G)
        const addInfoRow = (label, value) => {
        const row = worksheet.addRow([label, value]);
        row.height = 20;

        // Merge kolom B sampai G untuk value agar sejajar lebar tabel
        const rn = row.number;
        worksheet.mergeCells(rn, 2, rn, TOTAL_COLS);

        // Label (kolom A)
        row.getCell(1).font      = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FF334155' } };
        row.getCell(1).fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
        row.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
        row.getCell(1).border    = thinBorder();

        // Value (kolom B, sudah di-merge ke G)
        row.getCell(2).font      = { name: 'Calibri', size: 11, color: { argb: 'FF0F172A' } };
        row.getCell(2).fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
        row.getCell(2).alignment = { vertical: 'middle', horizontal: 'left', wrapText: false };
        row.getCell(2).border    = thinBorder();
        };

        // Tambahkan baris info project
        addInfoRow('Judul Project',   displayTitle);
        addInfoRow('Project Manager', projectManagerName);
        addInfoRow('Status',          computedStatusCfg.label);
        addInfoRow('Due Date',        fmtDate(displayDueDate));
        addInfoRow('Team Members',    teamMembersStr);

        // ── Baris kosong pemisah ──
        worksheet.addRow([]);

        // ── Header tabel kolom ──
        const colHeaders = ['TASK', 'PIC', 'PIC EXTERNAL', 'STATUS', 'SCHEDULE DATE', 'ACTUAL DATE', 'PROGRESS', 'DURASI'];
        const headerRow  = worksheet.addRow(colHeaders);
        headerRow.height = 22;
        headerRow.eachCell((cell) => {
            cell.font      = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentArgb } };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border    = {
                top:    { style: 'thin', color: { argb: 'FFFFFFFF' } },
                left:   { style: 'thin', color: { argb: 'FFFFFFFF' } },
                bottom: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                right:  { style: 'thin', color: { argb: 'FFFFFFFF' } },
            };
        });

        // ── Baris data task ──
        tasks.forEach((task, idx) => {
            const splitPic = task.pic ? task.pic.split('-').map(s => s.trim()) : [];
            const picDisplay = splitPic.length > 0 ? splitPic.slice(1).join(' / ') : task.pic;
            const picExternalDisplay = task.picExternal ? task.picExternal.split('-').map(s => s.trim()).join(' / ') : '-';

            const rowData = [
                task.name || '-',
                picDisplay || task.pic || '-',
                picExternalDisplay,
                getStatusLabel(task.status),
                fmtRange(task.scheduleStart, task.scheduleEnd),
                fmtRange(task.actualStart, task.actualEnd),
                `${task.progress || 0}%`,
                hitungDurasi(task.actualStart, task.actualEnd),
            ];

            const dataRow = worksheet.addRow(rowData);
            dataRow.height = 20;

            // Warna baris selang-seling (zebra striping) untuk keterbacaan
            const rowFill = idx % 2 === 0
                ? { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } }
                : { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };

            dataRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                cell.font      = { name: 'Calibri', size: 11, color: { argb: 'FF334155' } };
                cell.fill      = rowFill;
                cell.border    = thinBorder();
                // Kolom PROGRESS dan STATUS: center; sisanya left
                cell.alignment = {
                vertical: 'middle',
                horizontal: (colNumber === 4 || colNumber === 7 || colNumber === 8) ? 'center' : 'left',
                wrapText: false,
                };
            });
        });

        // ── Generate & download file ──
        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const timestamp  = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
        const categoryName = category.name || 'Project';
        const fileName   = `${categoryName}_Export_${timestamp}.xlsx`;

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), fileName);

    } catch (error) {
        console.error('Gagal mengekspor Excel:', error);
        alert('Gagal mengekspor file Excel. Silakan coba lagi.');
    }
};

// Helper: format schedule/actual date range dari dua field tanggal
const fmtRange = (start, end) => {
    if (!start && !end) return '-';
    return `${fmtDate(start)} - ${fmtDate(end)}`;
};

// Helper: mapping status app ke label teks
const getStatusLabel = (status) => {
    return STATUS_CFG[status]?.label || '-';
};

// Konversi hex color (#rrggbb) ke format ARGB yang dipakai ExcelJS (FFRRGGBB)
const toArgb = (hex) => {
    const clean = hex.replace('#', '');
    return `FF${clean.toUpperCase()}`;
};

// Helper: hitung durasi actual date
// - Jika start/end kosong → "0 hari"
// - Jika ≤ 30 hari       → "X hari"
// - Jika > 30 hari       → "X bulan" (dibulatkan)
const hitungDurasi = (start, end) => {
    if (!start || !end) return '0 hari';
    const tglStart = new Date(start);
    const tglEnd   = new Date(end);
    if (isNaN(tglStart) || isNaN(tglEnd)) return '0 hari';
    const selisihMs   = tglEnd - tglStart;
    if (selisihMs < 0) return '0 hari';
    // +1 agar inklusif: hari mulai dan hari selesai sama-sama dihitung
    const selisihHari = Math.floor(selisihMs / (1000 * 60 * 60 * 24)) + 1;
    if (selisihHari < 30) return `${selisihHari} hari`;
    const bulan    = Math.floor(selisihHari / 30);
    const sisaHari = selisihHari % 30;
    // Jika ada sisa hari tampilkan "X bulan Y hari", jika pas tampilkan "X bulan"
    return sisaHari > 0 ? `${bulan} bulan ${sisaHari} hari` : `${bulan} bulan`;
};

// Border tipis reusable
const thinBorder = (argbColor = 'FFE2E8F0') => ({
    top:    { style: 'thin', color: { argb: argbColor } },
    left:   { style: 'thin', color: { argb: argbColor } },
    bottom: { style: 'thin', color: { argb: argbColor } },
    right:  { style: 'thin', color: { argb: argbColor } },
});